import React, { useMemo } from 'react';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined';
import PolylineIcon from '@mui/icons-material/Polyline';
import RoomIcon from '@mui/icons-material/Room';
import { Card } from '@components/atoms/Card';
import { Avatar } from '@components/atoms/Avatar';
import { Button } from '@components/atoms/Button';
import { StarToggle } from '@components/atoms/StarToggle';
import GoogleMapsLinkButton from '@components/atoms/GoogleMapsLinkButton';
import { Project } from '@models/project.model';
import { ProjectStatusBadge } from '@components/atoms/ProjectStatusBadge';
import { ExpandableText } from '@components/molecules/ExpandableText';
import { toast } from 'react-toastify';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import { useGeofences } from '@hooks/api/geofence';
import { GeofenceShapeType } from '@models/geofence.model';
import { cn } from '@utils/cn';
import { useTranslation } from '@hooks/useTranslation';
import { Badge } from '@components/atoms/Badge';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { useHistory } from 'react-router-dom';

interface ProjectCardProps {
  project: Project;
  onEdit?: () => void;
  onDelete?: () => void;
  onShowGeofence?: (geofenceId?: string) => void;
  onToggleFavorite?: () => void;
  goToClient?: (clientId: string) => void;
  goToSubcontractor?: (subcontractorId: string) => void;
  // Hides edit/delete affordances (e.g. for read-only reviewers)
  readOnly?: boolean;
  // Reviewers may SEE the client/subcontractor names but must not navigate to
  // the Clients/Subcontractors pages (which their role cannot access).
  isReviewer?: boolean;
  // Undefined hides the affordance — same convention as onEdit/onDelete. The parent
  // (`projects.tsx`) passes this only when the viewer's role is SUPERINTENDENT+; no role
  // logic lives in this card itself.
  onReportIncident?: () => void;
  /** Sprint 37, story-199. Extra header action (the assistant button), rendered first in the actions row. */
  headerAction?: React.ReactNode;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({
  onEdit,
  project,
  onDelete,
  onShowGeofence,
  onToggleFavorite,
  goToClient,
  goToSubcontractor,
  readOnly = false,
  isReviewer = false,
  onReportIncident,
  headerAction,
}) => {
  const tActionMessages = useTranslation('actionMessages');
  const t = useTranslation('incidents');
  const tProjects = useTranslation('projects');
  const history = useHistory();

  // Sprint 25, story-129. Mirror project (delegated to us) vs. the GC's original that carries a
  // legacy same-org self-mirror (GC and sub are the same org — folded server-side, exposed here
  // via `selfMirror` instead of a second unlabelled row).
  const isDelegatedMirror = !!project.originalProjectId;
  const delegatingOrgName = project.originalProject?.organization?.name;
  const { data: geofences = [] } = useGeofences(
    project?.projectId ? { projectId: project.projectId } : undefined,
  );

  const handleCopyProjectId = (projectId: string) => {
    navigator.clipboard.writeText(projectId);
    toast.success(tActionMessages.projectIdCopiedToClipboard);
  };

  const uniqueSubcontractors = useMemo(() => {
    const seen = new Set<string>();
    const result: { id: string; name: string }[] = [];
    if (!project?.tasks) return result;
    for (const task of project.tasks) {
      const id =
        (task.delegateOrganizationId as string | null) ||
        (task.delegateOrganization?.organizationId as string | undefined) ||
        null;
      if (id && !seen.has(id)) {
        seen.add(id);
        result.push({
          id,
          name: task.delegateOrganization?.name || 'Unknown Organization',
        });
      }
    }
    return result;
  }, [project.tasks]);

  return (
    <Card
      data-testid="project-card"
      className="min-w-64 h-full overflow-y-auto no-scrollbar">
      {isDelegatedMirror && delegatingOrgName && (
        <div
          data-testid="delegation-banner"
          className="flex items-start gap-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 p-3 mb-4 text-sm text-indigo-900 dark:text-indigo-100">
          <SyncAltIcon sx={{ fontSize: '18px' }} className="mt-0.5 flex-shrink-0" />
          <span>
            {(
              tProjects?.delegatedBanner ||
              'Delegated by {org} · their project "{project}". You run crews, materials, equipment and time here; {org} tracks progress from their side.'
            )
              .replaceAll('{org}', delegatingOrgName)
              .replace('{project}', project.originalProject?.name || project.name)}
          </span>
        </div>
      )}
      <div className="flex justify-between">
        <Avatar
          size="xl"
          alt={project.name}
          text={project.name}
          src={project.siteImage}
        />
        {/* Actions only. Favourite used to sit here too, which put four controls in a
            256px-wide card with no room to breathe. It moved down beside the status badge:
            it marks what the project IS to you, not something you do to it, so it belongs
            with the state rather than with edit and delete. */}
        <div className="flex items-start gap-1">
          {headerAction}
          {onShowGeofence && (
            <Button
              variant="tertiary"
              onlyIcon
              onClick={() => onShowGeofence()}
              title="View geofence"
            >
              <PinDropOutlinedIcon sx={{ fontSize: '16px' }} />
            </Button>
          )}
          {!readOnly && (
            <Button
              variant="tertiary"
              onlyIcon
              onClick={onEdit}
              title="Edit project"
              data-testid="project-card-edit-button"
            >
              <EditOutlinedIcon sx={{ fontSize: '16px' }} />
            </Button>
          )}
          {!readOnly && (!project.tasks || project.tasks.length === 0) && (
            <Button
              variant="tertiary"
              onlyIcon
              onClick={onDelete}
              title="Delete project"
              data-testid="project-card-delete-button"
            >
              <DeleteOutlinedIcon sx={{ fontSize: '16px' }} />
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-2 mt-4">
          <h1 className="text-sm text-gray-900 font-medium">Project</h1>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {isDelegatedMirror && (
              <Badge variant="secondary" size="sm">
                {tProjects?.badgeDelegated || 'Delegated'}
              </Badge>
            )}
            {project.state && (
              <ProjectStatusBadge
                state={project.state}
                isBehind={project.isBehind}
              />
            )}
            {!readOnly && onToggleFavorite && (
              <StarToggle
                active={!!project.isFavorited}
                onToggle={onToggleFavorite}
              />
            )}
          </div>
        </div>
        <p className="text-2xl font-bold capitalize" data-testid="project-card-name">{project.name}</p>
      </div>

      {project.description && (
        <div className="flex flex-col gap-1">
          <h1 className="text-sm mt-4 text-gray-900 font-medium">Description</h1>
          <ExpandableText text={project.description} clampLines={3} />
        </div>
      )}

      <div className="flex flex-col gap-1">
        <h1 className="text-sm mt-4 text-gray-900 font-medium">Address</h1>
        <p className="text-sm text-gray-500">{project.address}</p>
      </div>

      {project.superintendents && project.superintendents.length > 0 && (
        <div className="flex flex-col gap-1" data-testid="project-card-superintendents">
          <h1 className="text-sm mt-4 text-gray-900 font-medium">
            Superintendents
          </h1>
          <div className="text-sm text-gray-500 capitalize">
            {project.superintendents.map((superintendent) => (
              <p key={superintendent.employeeId}>
                {superintendent.user?.firstName} {superintendent.user?.lastName}
              </p>
            ))}
          </div>
        </div>
      )}

      {project.projectManager && (
        <div className="flex flex-col gap-1">
          <h1 className="text-sm mt-4 text-gray-900 font-medium">
            Project Manager
          </h1>
          <p className="text-sm text-gray-500 capitalize">
            {project.projectManager?.user?.firstName}{' '}
            {project.projectManager?.user?.lastName}
          </p>
        </div>
      )}

      {project.client && (
        <div className="flex flex-col gap-1">
          <h1 className="text-sm mt-4 text-gray-900 font-medium">Client</h1>
          {isReviewer ? (
            <p className="text-base text-gray-900 capitalize">
              {project.client?.name}
            </p>
          ) : (
            <p
              onClick={() => goToClient?.(project.clientId as string)}
              className="text-base text-indigo-900 cursor-pointer capitalize">
              {project.client?.name}
            </p>
          )}
        </div>
      )}

      {(uniqueSubcontractors.length > 0 || project.selfMirror) && (
        <div className="flex flex-col">
          <h1 className="text-sm mt-4 text-gray-900 font-medium">
            Subcontractors
          </h1>
          {uniqueSubcontractors.map((org) =>
            isReviewer ? (
              <p key={org.id} className="text-base text-gray-900 capitalize">
                {org.name}
              </p>
            ) : (
              <p
                key={org.id}
                onClick={() => goToSubcontractor?.(org.id)}
                className="text-base text-indigo-900 cursor-pointer capitalize">
                {org.name}
              </p>
            ),
          )}
          {/* Sprint 25, story-129. Legacy same-org self-mirror (GC bid on/was delegated its own
              request) — never a second unlabelled row in the main list; reachable only from here. */}
          {project.selfMirror && !isReviewer && (
            <p
              onClick={() => history.push(`/projects/${project.selfMirror?.projectId}`)}
              className="text-base text-indigo-900 cursor-pointer">
              {tProjects?.selfMirrorLink || 'Your organization — delegated copy'} →
            </p>
          )}
          {project.selfMirror && isReviewer && (
            <p className="text-base text-gray-900">
              {tProjects?.selfMirrorLink || 'Your organization — delegated copy'}
            </p>
          )}
        </div>
      )}
      <div className="flex flex-col gap-1">
        <h1 className="text-sm mt-4 text-gray-900 font-medium">Project ID#</h1>
        <p
          onClick={() => handleCopyProjectId(project.projectId as string)}
          className="text-sm text-orange-500 cursor-pointer">
          {project.projectId}
        </p>
        {typeof project.lat === 'number' &&
          typeof project.long === 'number' && (
            <div className="mt-2">
              <GoogleMapsLinkButton
                lat={project.lat}
                lng={project.long}
                label="Copy directions"
              />
            </div>
          )}
      </div>

      {geofences.length > 0 && (
        <div className="flex flex-col gap-1">
          <h1 className="text-sm mt-4 text-gray-900 font-medium">Geofences</h1>
          <ul className="flex flex-col gap-1 mt-1">
            {geofences.map((g) => {
              const ShapeIcon =
                g.shape.type === GeofenceShapeType.POLYGON
                  ? PolylineIcon
                  : RoomIcon;
              const tint = g.category?.color || g.color || '#3B82F6';
              return (
                <li key={g.geofenceId}>
                  <button
                    type="button"
                    onClick={() => onShowGeofence?.(g.geofenceId)}
                    className={cn(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left',
                      'hover:bg-gray-50 transition-colors',
                    )}
                  >
                    <span
                      className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: `${tint}20`,
                        color: tint,
                      }}
                    >
                      <ShapeIcon sx={{ fontSize: 14 }} />
                    </span>
                    <span className="text-sm text-gray-700 truncate flex-1">
                      {g.name}
                    </span>
                    {!g.isActive && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        Off
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {onReportIncident && (
        <div className="mt-4">
          <Button
            variant="secondary"
            fullWidth
            leftIcon={<ReportProblemRoundedIcon sx={{ fontSize: '16px' }} />}
            onClick={onReportIncident}
            data-testid="project-view-report-incident-button">
            {t?.reportIncident || 'Report Incident'}
          </Button>
        </div>
      )}
    </Card>
  );
};
